import React from 'react';
import { X, ArrowLeft, Briefcase, CheckCircle } from 'lucide-react';

export default function UseCases({ onClose }) {
    const cases = [
        {
            industry: "Healthcare Data",
            title: "Healthcare Data Fabric",
            challenge: "Hospitals struggle with 100s of fragmented systems, leading to inconsistent patient data and poor decision-making.",
            solution: "Developed a semantic-enriched data fabric featuring a no-code gateway builder and workflow engine.",
            results: [
                "Unified hundreds of disparate systems",
                "Enabled no-code analytics creation",
                "Eliminated patient data inconsistency"
            ],
            emoji: "🏥",
            color: "from-blue-50 to-cyan-50",
            borderColor: "border-blue-200"
        },
        {
            industry: "Public Transport",
            title: "Railway Digital Twin",
            challenge: "Tokyo's complex network (16M daily passengers) needed better demand forecasting and crowd estimation.",
            solution: "Built a generative AI model to simulate travel patterns, station crowds, and 'what-if' timetable scenarios.",
            results: [
                "Modeled 16M+ daily passenger journeys",
                "Precise station-level crowd forecasting",
                "Optimized commercial ad timing"
            ],
            emoji: "🚅",
            color: "from-indigo-50 to-violet-50",
            borderColor: "border-indigo-200"
        },
        {
            industry: "Defence & Resilience",
            title: "Resilient Infrastructure",
            challenge: "Critical defence systems cannot afford catastrophic failure; 'deadly states' must be found before adversaries find them.",
            solution: "Created world first AI powered infrastructure chaos simulation platform to autonomously find and replicate system failures.",
            results: [
                "Autonomous failure mode discovery",
                "Proactive identification of 'deadly states'",
                "Realistic simulation of catastrophic outages"
            ],
            emoji: "🛡️",
            color: "from-orange-50 to-red-50",
            borderColor: "border-orange-200"
        },
        {
            industry: "Cybersecurity",
            title: "Dynamic Attack Surface Monitor",
            challenge: "Quantifying the constantly changing attack surface for corporate and government software systems.",
            solution: "Deployed ML & NLP models with CyBots to audit API layers, code security, and T&C data privacy risks.",
            results: [
                "Automated software vulnerability detection",
                "Real-time API layer analysis",
                "Quantified data privacy exposure"
            ],
            emoji: "🔒",
            color: "from-slate-50 to-gray-50",
            borderColor: "border-slate-200"
        },
        {
            industry: "Mental Health",
            title: "Bipolar Companion",
            challenge: "Patients with Bipolar Disorder require timely, personalized engagement to manage mood and coping strategies.",
            solution: "Built an AI Companion with world top bipolar disorder specialists that tracks mood/coping and recommends personalized intervention strategies.",
            results: [
                "Personalized AI intervention pathways",
                "Real-time digital mood tracking",
                "Enhanced patient self-management"
            ],
            emoji: "🧠",
            color: "from-purple-50 to-fuchsia-50",
            borderColor: "border-purple-200"
        },
        {
            industry: "IoT & Logistics",
            title: "Indoor Tracking System",
            challenge: "Difficulty tracking equipment and users across multiple systems without expensive structural changes.",
            solution: "Implemented a Seamless BLE Beacon system for infrastructure-free localization and asset visualization.",
            results: [
                "Structure-free implementation",
                "Real-time user & asset localization",
                "Cross-system data visualization"
            ],
            emoji: "📍",
            color: "from-green-50 to-emerald-50",
            borderColor: "border-green-200"
        },
        {
            industry: "Chronic Care",
            title: "iMOVE Diabetes Management",
            challenge: "Encouraging activity and reducing sedentary behavior in patients with Type 2 Diabetes.",
            solution: "Integrated wearable devices with ML activity recognition models and mobile apps to nudge patient behavior.",
            results: [
                "High-accuracy activity recognition",
                "Reduction in sedentary behavior",
                "Seamless wearable integration"
            ],
            emoji: "⌚",
            color: "from-teal-50 to-cyan-50",
            borderColor: "border-teal-200"
        },
        {
            industry: "Social Impact",
            title: "Lilibot Responsible Gambling",
            challenge: "80% of youth engage in gambling; need for accessible, non-judgmental support and risk education.",
            solution: "Developed an inclusive LLM conversational interface for Gov to provide support and self-assessment tools.",
            results: [
                "Inclusive, judgment-free support",
                "Accessible via conversational AI",
                "Youth-focused risk education"
            ],
            emoji: "💬",
            color: "from-amber-50 to-yellow-50",
            borderColor: "border-amber-200"
        }
    ];
    const testimonials = [];

    return (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-gray-700 hover:text-amber-600 transition"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-medium">Back</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="text-gray-700 hover:text-amber-600 transition"
                    >
                        <X size={24} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                {/* Hero */}
                <div className="mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full border border-amber-300 mb-6">
                        <Briefcase size={16} className="text-amber-600" />
                        <span className="text-amber-900 text-sm font-semibold">Use Cases & Success Stories</span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Real Impact, <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Real Results</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                        See how organizations across industries are leveraging Deep 9's synthetic sapience solutions to achieve transformative outcomes.
                    </p>
                </div>

                {/* Use Cases */}
                <section className="mb-16">
                    <div className="space-y-8">
                        {cases.map((useCase, idx) => (
                            <div key={idx} className={`bg-gradient-to-br ${useCase.color} rounded-3xl p-8 md:p-12 border ${useCase.borderColor}`}>
                                <div className="flex items-start gap-6 mb-6">
                                    <div className="text-6xl">{useCase.emoji}</div>
                                    <div className="flex-1">
                                        <div className="text-sm font-semibold text-amber-600 mb-2">{useCase.industry}</div>
                                        <h3 className="text-3xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-3 gap-8">
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Challenge</h4>
                                        <p className="text-gray-700 leading-relaxed">{useCase.challenge}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Solution</h4>
                                        <p className="text-gray-700 leading-relaxed">{useCase.solution}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Results</h4>
                                        <ul className="space-y-2">
                                            {useCase.results.map((result, ridx) => (
                                                <li key={ridx} className="flex items-start gap-2">
                                                    <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                                                    <span className="text-gray-700">{result}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>


                {/* Industries We Serve */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Industries We Transform</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {["Healthcare", "Education", "Finance", "Manufacturing", "Retail", "Government", "Energy", "Transportation"].map((industry, idx) => (
                            <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 text-center hover:border-amber-300 transition">
                                <p className="font-semibold text-gray-900">{industry}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CTA */}
                <section className="text-center bg-gradient-to-r from-amber-600 via-orange-600 to-amber-600 rounded-3xl p-12 text-white">
                    <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Organization?</h2>
                    <p className="text-xl text-amber-50 mb-8 max-w-2xl mx-auto">
                        Let's discuss how Deep 9 can help you achieve similar results in your industry.
                    </p>
                    <button className="px-8 py-4 bg-white text-amber-600 rounded-xl font-bold text-lg hover:shadow-2xl transition">
                        Schedule a Consultation
                    </button>
                </section>
            </div>
        </div>
    );
}
